import { Eyebrow } from "../atoms/Eyebrow";
import { Reveal } from "../atoms/Reveal";
import type { Copy } from "../lib/copy";

type ProductsProps = { t: Copy };

export function Products({ t }: ProductsProps) {
  return (
    <section id="products" className="section products">
      <div className="container">
        <div className="section-head wide">
          <Reveal>
            <Eyebrow>{t.products.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal as="h2" className="section-title" delay={60}>
            <span className="ink">{t.products.title_a}</span>{" "}
            <span className="serif-accent">{t.products.title_b}</span>
          </Reveal>
          <Reveal as="p" className="section-sub" delay={120}>
            {t.products.sub}
          </Reveal>
        </div>

        <div className="product-grid">
          {t.products.items.map((p, i) => (
            <Reveal key={i} className={"product-card prod-" + i} delay={i * 70}>
              <div className="pc-head">
                <span className="pc-num">{p.num}</span>
                <span className="pc-name">{p.name}</span>
              </div>
              <h3 className="pc-title">{p.title}</h3>
              <p className="pc-body">{p.body}</p>
              <div className="pc-visual">
                <ProductViz index={i} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * One unique visualization per product card:
 *   0 Core     — hub + spokes (PMS/Voice/Email/Web)
 *   1 Voice    — animated waveform
 *   2 Flows    — DAG (trigger → PMS/Email → action)
 *   3 Insights — animated bar chart
 */
function ProductViz({ index }: { index: number }) {
  if (index === 0) {
    return (
      <svg viewBox="0 0 300 140" className="pv" aria-hidden="true">
        <circle cx="150" cy="70" r="10" className="pv-node center" />
        {(
          [
            [40, 30, "PMS"],
            [260, 30, "Voice"],
            [40, 110, "Email"],
            [260, 110, "Web"],
          ] as const
        ).map(([x, y, label], i) => (
          <g key={i}>
            <line x1="150" y1="70" x2={x} y2={y} className="pv-line" />
            <circle cx={x} cy={y} r="6" className="pv-node" />
            <text x={x} y={y - 12} className="pv-label" textAnchor="middle">
              {label}
            </text>
          </g>
        ))}
      </svg>
    );
  }
  if (index === 1) {
    return (
      <div className="pv-wave" aria-hidden="true">
        {Array.from({ length: 28 }).map((_, i) => (
          <span key={i} style={{ animationDelay: i * 70 + "ms" }} />
        ))}
      </div>
    );
  }
  if (index === 2) {
    return (
      <svg viewBox="0 0 300 140" className="pv" aria-hidden="true">
        <g className="pv-flow">
          <rect x="10" y="55" width="56" height="30" rx="6" className="flow-node" />
          <text x="38" y="74" className="pv-label" textAnchor="middle">
            Trigger
          </text>
          <path d="M66 70 C 90 70, 100 30, 130 30" className="flow-line" />
          <path d="M66 70 C 90 70, 100 110, 130 110" className="flow-line" />
          <rect x="130" y="15" width="56" height="30" rx="6" className="flow-node" />
          <text x="158" y="34" className="pv-label" textAnchor="middle">
            PMS
          </text>
          <rect x="130" y="95" width="56" height="30" rx="6" className="flow-node" />
          <text x="158" y="114" className="pv-label" textAnchor="middle">
            Email
          </text>
          <path d="M186 30 C 210 30, 220 70, 234 70" className="flow-line" />
          <path d="M186 110 C 210 110, 220 70, 234 70" className="flow-line" />
          <rect x="234" y="55" width="56" height="30" rx="6" className="flow-node done" />
          <text x="262" y="74" className="pv-label" textAnchor="middle">
            Action
          </text>
        </g>
      </svg>
    );
  }
  // Insights bar chart
  return (
    <div className="pv-bars" aria-hidden="true">
      {[30, 52, 48, 70, 64, 82, 78, 92, 88].map((h, i) => (
        <span key={i} style={{ height: h + "%", animationDelay: i * 60 + "ms" }} />
      ))}
    </div>
  );
}
